/**
 * useAudioAnalyser — connects an HTMLAudioElement to the Web Audio API
 * AnalyserNode so callers can read live FFT data on every animation frame.
 *
 * Returns { levels, ready }:
 *   • levels — Uint8Array of length `bars`. Each entry is a 0–255 amplitude
 *     for one frequency bucket, weighted to emphasise the bass. Updated on
 *     every requestAnimationFrame while the audio is playing.
 *   • ready  — true once the AudioContext is wired and resumed.
 *
 * Resilient to:
 *   • CORS-tainted streams (skips wiring instead of throwing).
 *   • Browser autoplay policy (lazily creates the AudioContext after the
 *     first user gesture; checks state and resumes if suspended).
 *   • The same <audio> element being passed to multiple hooks (caches the
 *     MediaElementAudioSourceNode on the element since each element can
 *     only ever have ONE source node).
 */
import { useEffect, useRef, useState } from 'react';

const _sourceCache = new WeakMap();

export function useAudioAnalyser(audioRef, { enabled = true, bars = 32, smoothing = 0.78 } = {}) {
  const [ready, setReady] = useState(false);
  const levelsRef = useRef(new Uint8Array(bars));
  const [, setTick] = useState(0); // trigger re-render at most ~30fps via timer

  useEffect(() => {
    if (!enabled) return undefined;
    const el = audioRef.current;
    if (!el) return undefined;

    let ctx = null;
    let analyser = null;
    let raf = 0;
    let lastFrame = 0;
    let detached = false;

    // Force CORS-safe playback so the audio source can be analysed.
    // (If a stream is already loaded without crossOrigin, setting it now
    // forces a reload — safe because the player re-syncs on every poll.)
    if (!el.crossOrigin) {
      try { el.crossOrigin = 'anonymous'; } catch { /* noop */ }
    }

    const setup = () => {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        ctx = new AudioCtx();
        analyser = ctx.createAnalyser();
        analyser.fftSize = 256;             // 128 frequency bins
        analyser.smoothingTimeConstant = smoothing;
        // One <audio> element can only have ONE MediaElementAudioSourceNode
        // attached for the lifetime of the element. Reuse if already created.
        let source = _sourceCache.get(el);
        if (!source) {
          source = ctx.createMediaElementSource(el);
          _sourceCache.set(el, source);
        }
        source.connect(analyser);
        analyser.connect(ctx.destination);
        setReady(true);
        loop();
      } catch (e) {
        // Most likely a CORS-tainted stream (createMediaElementSource throws).
        // Silently bail — caller will fall back to the CSS-driven animation.
        ctx = null;
        analyser = null;
      }
    };

    const loop = () => {
      if (detached || !analyser) return;
      const now = performance.now();
      // Throttle React re-renders to ~30fps so we don't burn the main thread.
      if (now - lastFrame >= 33) {
        lastFrame = now;
        const data = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(data);
        // Map `bars` evenly across the LOWER 60% of the spectrum so visuals
        // are dominated by bass + low-mids (the parts humans feel as "punch")
        const out = new Uint8Array(bars);
        const usable = Math.floor(data.length * 0.6);
        for (let i = 0; i < bars; i++) {
          const start = Math.floor((i / bars) * usable);
          const end = Math.floor(((i + 1) / bars) * usable);
          let max = 0;
          for (let j = start; j <= end; j++) {
            if (data[j] > max) max = data[j];
          }
          // Emphasise lowest 25% of bars (treated as "bass") with a 1.15× boost.
          const boost = i < bars * 0.25 ? 1.15 : 1.0;
          out[i] = Math.min(255, Math.floor(max * boost));
        }
        levelsRef.current = out;
        setTick((t) => (t + 1) & 0xfff);
      }
      raf = requestAnimationFrame(loop);
    };

    // The AudioContext can only be created after a user gesture in most
    // browsers. We attach a one-shot listener on the audio element's `play`
    // event AND on document interaction.
    const kick = () => {
      if (ctx) {
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
        return;
      }
      setup();
    };
    el.addEventListener('play', kick);
    el.addEventListener('playing', kick);
    document.addEventListener('pointerdown', kick, { once: true });

    // If audio is already playing when the hook mounts, kick immediately.
    if (!el.paused) kick();

    return () => {
      detached = true;
      cancelAnimationFrame(raf);
      el.removeEventListener('play', kick);
      el.removeEventListener('playing', kick);
      document.removeEventListener('pointerdown', kick);
      if (ctx) {
        try { ctx.close(); } catch { /* noop */ }
      }
    };
  }, [audioRef, enabled, bars, smoothing]);

  return { levels: levelsRef.current, ready };
}

export default useAudioAnalyser;
